import React, { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Tag, Ticket, Plus, Loader2, X } from "lucide-react";
import authService from "../../services/authService";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api/social`;

/**
 * Tela do fornecedor para criar campanhas e cupons promocionais.
 *
 * ACHADO DA REVISÃO DE ORGANIZAÇÃO: o backend inteiro (social_commerce_routes.py)
 * já existia e funcionava, mas nenhuma tela do fornecedor usava - a
 * funcionalidade existia mas era impossível de usar na prática.
 */
const CampanhasCupons = () => {
  const fornecedorId = authService.getUser()?.id || "";

  const [abaAtiva, setAbaAtiva] = useState("campanhas");
  const [carregando, setCarregando] = useState(true);
  const [campanhas, setCampanhas] = useState([]);
  const [cupons, setCupons] = useState([]);

  const [modalCampanha, setModalCampanha] = useState(false);
  const [modalCupom, setModalCupom] = useState(false);
  const [salvando, setSalvando] = useState(false);

  const [novaCampanha, setNovaCampanha] = useState({
    nome: "", descricao: "", desconto_percentual: "", data_inicio: "", data_fim: ""
  });
  const [novoCupom, setNovoCupom] = useState({ codigo: "", desconto_percentual: "", validade: "", limite_uso: "" });

  const carregarDados = async () => {
    setCarregando(true);
    try {
      const [respCamp, respCup] = await Promise.all([
        authService.fetchAuth(`${API}/campanhas/fornecedor/${fornecedorId}`),
        authService.fetchAuth(`${API}/cupons/fornecedor/${fornecedorId}`),
      ]);
      if (respCamp.ok) setCampanhas(await respCamp.json());
      if (respCup.ok) setCupons(await respCup.json());
    } catch (error) {
      console.error("Erro ao carregar campanhas/cupons:", error);
    } finally {
      setCarregando(false);
    }
  };

  useEffect(() => {
    carregarDados();
  }, []);

  const criarCampanha = async () => {
    if (!novaCampanha.nome || !novaCampanha.desconto_percentual || !novaCampanha.data_inicio || !novaCampanha.data_fim) {
      toast.error("Preencha todos os campos obrigatórios.");
      return;
    }
    setSalvando(true);
    try {
      const resp = await authService.fetchAuth(`${API}/campanhas/criar`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fornecedor_id: fornecedorId,
          nome: novaCampanha.nome,
          descricao: novaCampanha.descricao,
          desconto_percentual: parseFloat(novaCampanha.desconto_percentual),
          data_inicio: new Date(novaCampanha.data_inicio).toISOString(),
          data_fim: new Date(novaCampanha.data_fim).toISOString(),
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.detail || "Erro ao criar campanha");
      toast.success("Campanha criada!");
      setModalCampanha(false);
      setNovaCampanha({ nome: "", descricao: "", desconto_percentual: "", data_inicio: "", data_fim: "" });
      await carregarDados();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSalvando(false);
    }
  };

  const criarCupom = async () => {
    if (!novoCupom.codigo || !novoCupom.desconto_percentual || !novoCupom.validade) {
      toast.error("Preencha código, desconto e validade.");
      return;
    }
    setSalvando(true);
    try {
      const resp = await authService.fetchAuth(`${API}/cupons/criar`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          fornecedor_id: fornecedorId,
          codigo: novoCupom.codigo,
          desconto_percentual: parseFloat(novoCupom.desconto_percentual),
          validade: new Date(novoCupom.validade).toISOString(),
          limite_uso: novoCupom.limite_uso ? parseInt(novoCupom.limite_uso, 10) : null,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(data.detail || "Erro ao criar cupom");
      toast.success("Cupom criado!");
      setModalCupom(false);
      setNovoCupom({ codigo: "", desconto_percentual: "", validade: "", limite_uso: "" });
      await carregarDados();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Tag className="w-7 h-7 text-orange-400" />
          <div>
            <h1 className="text-2xl font-bold text-white">Campanhas e Cupons</h1>
            <p className="text-sm text-gray-400">Promoções para os afiliados usarem na divulgação</p>
          </div>
        </div>
        <Button
          onClick={() => (abaAtiva === "campanhas" ? setModalCampanha(true) : setModalCupom(true))}
          className="bg-orange-600 hover:bg-orange-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          {abaAtiva === "campanhas" ? "Nova Campanha" : "Novo Cupom"}
        </Button>
      </div>

      <div className="flex gap-2 mb-6 border-b border-gray-700">
        <button
          onClick={() => setAbaAtiva("campanhas")}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            abaAtiva === "campanhas" ? "border-orange-500 text-orange-400" : "border-transparent text-gray-400 hover:text-white"
          }`}
        >
          <Tag className="w-4 h-4" /> Campanhas
        </button>
        <button
          onClick={() => setAbaAtiva("cupons")}
          className={`flex items-center gap-2 px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
            abaAtiva === "cupons" ? "border-orange-500 text-orange-400" : "border-transparent text-gray-400 hover:text-white"
          }`}
        >
          <Ticket className="w-4 h-4" /> Cupons
        </button>
      </div>

      {carregando ? (
        <div className="flex items-center justify-center py-16 text-gray-400">
          <Loader2 className="w-6 h-6 animate-spin mr-2" />
          Carregando...
        </div>
      ) : abaAtiva === "campanhas" ? (
        <div className="space-y-2">
          {campanhas.length === 0 && (
            <Card className="p-8 text-center text-gray-400 bg-gray-800/50 border-gray-700">
              Nenhuma campanha criada ainda.
            </Card>
          )}
          {campanhas.map((c) => (
            <Card key={c.id} className="p-4 bg-gray-800/50 border-gray-700 flex items-center justify-between">
              <div>
                <p className="text-white font-medium">{c.nome}</p>
                <p className="text-xs text-gray-500">{c.descricao}</p>
              </div>
              <span className="text-green-400 font-bold">{c.desconto_percentual}% OFF</span>
            </Card>
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {cupons.length === 0 && (
            <Card className="p-8 text-center text-gray-400 bg-gray-800/50 border-gray-700">
              Nenhum cupom criado ainda.
            </Card>
          )}
          {cupons.map((c) => (
            <Card key={c.id} className="p-4 bg-gray-800/50 border-gray-700 flex items-center justify-between">
              <div>
                <p className="text-white font-medium font-mono">{c.codigo}</p>
                <p className="text-xs text-gray-500">Usos: {c.usos}{c.limite_uso ? ` / ${c.limite_uso}` : ""}</p>
              </div>
              <span className="text-green-400 font-bold">{c.desconto_percentual}% OFF</span>
            </Card>
          ))}
        </div>
      )}

      {modalCampanha && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center overflow-y-auto py-8 z-50 p-4">
          <Card className="p-6 max-w-md w-full bg-gray-900 border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-white">Nova Campanha</h3>
              <button onClick={() => setModalCampanha(false)}><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <div className="space-y-3">
              <Input placeholder="Nome da campanha" value={novaCampanha.nome}
                onChange={(e) => setNovaCampanha({ ...novaCampanha, nome: e.target.value })} />
              <Input placeholder="Descrição" value={novaCampanha.descricao}
                onChange={(e) => setNovaCampanha({ ...novaCampanha, descricao: e.target.value })} />
              <Input type="number" placeholder="Desconto (%)" value={novaCampanha.desconto_percentual}
                onChange={(e) => setNovaCampanha({ ...novaCampanha, desconto_percentual: e.target.value })} />
              <div className="grid grid-cols-2 gap-2">
                <Input type="date" value={novaCampanha.data_inicio}
                  onChange={(e) => setNovaCampanha({ ...novaCampanha, data_inicio: e.target.value })} />
                <Input type="date" value={novaCampanha.data_fim}
                  onChange={(e) => setNovaCampanha({ ...novaCampanha, data_fim: e.target.value })} />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setModalCampanha(false)}>Cancelar</Button>
              <Button onClick={criarCampanha} disabled={salvando} className="bg-orange-600 hover:bg-orange-700">
                {salvando ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Criar
              </Button>
            </div>
          </Card>
        </div>
      )}

      {modalCupom && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center overflow-y-auto py-8 z-50 p-4">
          <Card className="p-6 max-w-md w-full bg-gray-900 border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-white">Novo Cupom</h3>
              <button onClick={() => setModalCupom(false)}><X className="w-5 h-5 text-gray-400" /></button>
            </div>
            <div className="space-y-3">
              <Input placeholder="Código (ex: PROMO10)" value={novoCupom.codigo}
                onChange={(e) => setNovoCupom({ ...novoCupom, codigo: e.target.value.toUpperCase() })} />
              <Input type="number" placeholder="Desconto (%)" value={novoCupom.desconto_percentual}
                onChange={(e) => setNovoCupom({ ...novoCupom, desconto_percentual: e.target.value })} />
              <Input type="date" value={novoCupom.validade}
                onChange={(e) => setNovoCupom({ ...novoCupom, validade: e.target.value })} />
              <Input type="number" placeholder="Limite de uso (opcional)" value={novoCupom.limite_uso}
                onChange={(e) => setNovoCupom({ ...novoCupom, limite_uso: e.target.value })} />
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setModalCupom(false)}>Cancelar</Button>
              <Button onClick={criarCupom} disabled={salvando} className="bg-orange-600 hover:bg-orange-700">
                {salvando ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Criar
              </Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default CampanhasCupons;
